// Web Audio API-based procedural SFX and music. No external files.
// All sounds synthesized on the fly.

let ctx = null;
let masterGain = null;
let musicGain = null;
let sfxGain = null;
let muted = false;
let musicNode = null;
let musicIntervalId = null;

function ensureCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      // v2.6: read persisted user volumes if present
      let m = 0.6, mu = 0.18, sf = 0.6;
      try {
        const pM = parseFloat(localStorage.getItem('friendslop.vol.master'));
        const pMu = parseFloat(localStorage.getItem('friendslop.vol.music'));
        const pSf = parseFloat(localStorage.getItem('friendslop.vol.sfx'));
        if (!isNaN(pM)) m = pM;
        if (!isNaN(pMu)) mu = pMu;
        if (!isNaN(pSf)) sf = pSf;
      } catch (e) {}
      masterGain = ctx.createGain();
      masterGain.gain.value = m;
      masterGain.connect(ctx.destination);
      musicGain = ctx.createGain();
      musicGain.gain.value = mu;
      musicGain.connect(masterGain);
      sfxGain = ctx.createGain();
      sfxGain.gain.value = sf;
      sfxGain.connect(masterGain);
    } catch (e) {
      // audio disabled
    }
  }
  if (ctx && ctx.state === 'suspended') ctx.resume();
  return ctx;
}

function beep({ freq = 440, dur = 0.08, type = 'square', gain = 0.5, slideTo, delay = 0 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t0 + dur);
  }
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(sfxGain);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noise({ dur = 0.15, gain = 0.35, highpass = 800 }) {
  const c = ensureCtx();
  if (!c) return;
  const bufferSize = Math.floor(c.sampleRate * dur);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filt = c.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = highpass;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  src.connect(filt).connect(g).connect(sfxGain);
  src.start();
  src.stop(c.currentTime + dur + 0.02);
}

export const sfx = {
  cardPlay() { beep({ freq: 620, dur: 0.08, type: 'triangle', gain: 0.25 }); beep({ freq: 900, dur: 0.05, type: 'triangle', gain: 0.15, delay: 0.05 }); },
  cardDraw() { beep({ freq: 380, dur: 0.06, type: 'sine', gain: 0.18 }); },
  attack() { noise({ dur: 0.09, gain: 0.35, highpass: 400 }); beep({ freq: 220, dur: 0.09, type: 'sawtooth', gain: 0.25, slideTo: 90 }); },
  block()  { beep({ freq: 300, dur: 0.14, type: 'square', gain: 0.3, slideTo: 500 }); },
  hit()    { noise({ dur: 0.12, gain: 0.4, highpass: 200 }); },
  heal()   { beep({ freq: 660, dur: 0.15, type: 'sine', gain: 0.25 }); beep({ freq: 880, dur: 0.15, type: 'sine', gain: 0.22, delay: 0.08 }); },
  energy() { beep({ freq: 520, dur: 0.06, type: 'square', gain: 0.22 }); beep({ freq: 780, dur: 0.06, type: 'square', gain: 0.22, delay: 0.06 }); },
  buff()   { beep({ freq: 440, dur: 0.1, type: 'triangle', gain: 0.25, slideTo: 660 }); },
  debuff() { beep({ freq: 220, dur: 0.15, type: 'sawtooth', gain: 0.25, slideTo: 130 }); },
  victory(){ [523, 659, 784, 1047].forEach((f,i) => beep({ freq: f, dur: 0.18, type: 'triangle', gain: 0.3, delay: i * 0.09 })); },
  defeat() { [400, 320, 240, 160].forEach((f,i) => beep({ freq: f, dur: 0.22, type: 'sawtooth', gain: 0.35, delay: i * 0.11 })); },
  click()  { beep({ freq: 800, dur: 0.03, type: 'square', gain: 0.18 }); },
  gold()   { beep({ freq: 900, dur: 0.05, type: 'triangle', gain: 0.22 }); beep({ freq: 1200, dur: 0.05, type: 'triangle', gain: 0.22, delay: 0.05 }); },
  levelup(){ [440, 554, 659, 880].forEach((f,i) => beep({ freq: f, dur: 0.12, type: 'triangle', gain: 0.28, delay: i * 0.06 })); },

  // v1.2 MAX LIFE: expanded palette
  hover()  { beep({ freq: 1400, dur: 0.02, type: 'sine', gain: 0.08 }); },
  attackCard() { beep({ freq: 320, dur: 0.05, type: 'square', gain: 0.22, slideTo: 140 }); noise({ dur: 0.06, gain: 0.2, highpass: 1000 }); },
  skillCard()  { beep({ freq: 700, dur: 0.09, type: 'triangle', gain: 0.2, slideTo: 1100 }); beep({ freq: 1100, dur: 0.05, type: 'sine', gain: 0.14, delay: 0.06 }); },
  powerCard()  { beep({ freq: 130, dur: 0.28, type: 'sawtooth', gain: 0.24, slideTo: 260 }); beep({ freq: 260, dur: 0.28, type: 'triangle', gain: 0.16, delay: 0.02, slideTo: 520 }); beep({ freq: 520, dur: 0.2, type: 'sine', gain: 0.14, delay: 0.1 }); },
  rareChime()  { [880, 1108, 1318].forEach((f,i) => beep({ freq: f, dur: 0.16, type: 'sine', gain: 0.22, delay: i * 0.05 })); },
  legendaryChime() {
    // Big multi-note stab
    [440, 554, 659, 880, 1108, 1318, 1760].forEach((f,i) => beep({ freq: f, dur: 0.32, type: 'triangle', gain: 0.22, delay: i * 0.04 }));
    noise({ dur: 0.4, gain: 0.14, highpass: 4000 });
  },
  // v2.9 DIVINE — celestial arpeggio + shimmer + bell tail (fully distinct from legendaryChime)
  // v3.0: accepts optional cardId for per-card stinger overlaid on the base chime.
  divineChime(cardId = null) {
    const c = ensureCtx(); if (!c) return;
    // Rising 4-note major-7 arpeggio (C, E, G, B, high C, high E)
    const notes = [523.25, 659.25, 783.99, 987.77, 1046.5, 1318.5, 1567.98, 2093];
    notes.forEach((f, i) => {
      beep({ freq: f, dur: 0.55, type: 'sine',     gain: 0.20, delay: i * 0.06 });
      beep({ freq: f, dur: 0.55, type: 'triangle', gain: 0.10, delay: i * 0.06 });
      // Add a shimmering perfect fifth harmonic
      beep({ freq: f * 1.5, dur: 0.35, type: 'sine', gain: 0.06, delay: i * 0.06 + 0.03 });
    });
    // Long bell tail
    beep({ freq: 2637, dur: 1.4, type: 'sine', gain: 0.10, delay: notes.length * 0.06 });
    beep({ freq: 3136, dur: 1.4, type: 'sine', gain: 0.08, delay: notes.length * 0.06 + 0.04 });
    // Angelic airy shimmer
    noise({ dur: 0.9, gain: 0.08, highpass: 6000 });
    noise({ dur: 0.4, gain: 0.05, highpass: 9000 });

    // v3.0: per-card stinger overlaid at t = 0.42s (mid-chime for punch)
    const stingerDelay = 0.42;
    const stingers = {
      divine_godmode:            () => { [880, 1174.66, 1568].forEach((f,i) => beep({ freq: f, dur: 0.4, type: 'sine',     gain: 0.16, delay: stingerDelay + i * 0.05 })); },
      divine_infinity:           () => { beep({ freq: 440, dur: 0.9, type: 'sine',   gain: 0.10, delay: stingerDelay, slideTo: 3520 }); beep({ freq: 220, dur: 0.9, type: 'sine', gain: 0.08, delay: stingerDelay, slideTo: 1760 }); },
      divine_brainrotprime:      () => { [110, 130.81, 164.81].forEach((f,i) => beep({ freq: f, dur: 0.5, type: 'sawtooth', gain: 0.22, delay: stingerDelay + i * 0.08 })); noise({ dur: 0.3, gain: 0.14, highpass: 100 }); },
      divine_ratiod:             () => { [800, 600, 400, 200].forEach((f,i) => beep({ freq: f, dur: 0.14, type: 'square', gain: 0.20, delay: stingerDelay + i * 0.06 })); },
      divine_singularity:        () => { beep({ freq: 60,  dur: 1.1, type: 'sawtooth', gain: 0.32, delay: stingerDelay, slideTo: 20 }); noise({ dur: 0.6, gain: 0.22, highpass: 40 }); },
      divine_touchgrass:         () => { [523, 659, 784, 1046, 1318].forEach((f,i) => beep({ freq: f, dur: 0.3, type: 'triangle', gain: 0.16, delay: stingerDelay + i * 0.06 })); },
      divine_rizzcascade:        () => { [880, 1108, 1318, 1760, 2217].forEach((f,i) => beep({ freq: f, dur: 0.14, type: 'sine', gain: 0.14, delay: stingerDelay + i * 0.04 })); noise({ dur: 0.2, gain: 0.06, highpass: 5000 }); },
      divine_sigma:              () => { beep({ freq: 1760, dur: 0.4, type: 'triangle', gain: 0.20, delay: stingerDelay }); beep({ freq: 880, dur: 0.6, type: 'sine', gain: 0.16, delay: stingerDelay + 0.05 }); },
      // Class-locked
      divine_rizzler_apex:       () => { [523, 659, 830, 1046].forEach((f,i) => beep({ freq: f, dur: 0.24, type: 'triangle', gain: 0.16, delay: stingerDelay + i * 0.05 })); },
      divine_sigma_ascended:     () => { beep({ freq: 82.4, dur: 0.9, type: 'sawtooth', gain: 0.28, delay: stingerDelay }); beep({ freq: 164.8, dur: 0.9, type: 'sawtooth', gain: 0.18, delay: stingerDelay + 0.05 }); },
      divine_gooner_immortal:    () => { [220, 260, 330, 440].forEach((f,i) => beep({ freq: f, dur: 0.3, type: 'sine', gain: 0.14, delay: stingerDelay + i * 0.05 })); },
    };
    if (cardId && stingers[cardId]) {
      try { stingers[cardId](); } catch (e) {}
    }
  },
  bossSting() {
    // Dramatic downward slam + boom
    [660, 440, 293, 195].forEach((f,i) => beep({ freq: f, dur: 0.4, type: 'sawtooth', gain: 0.32, delay: i * 0.09 }));
    beep({ freq: 55, dur: 0.9, type: 'sine', gain: 0.35, delay: 0.3, slideTo: 30 });
    noise({ dur: 0.25, gain: 0.35, highpass: 60 });
  },
  slam() {
    beep({ freq: 90, dur: 0.18, type: 'sine', gain: 0.5, slideTo: 30 });
    noise({ dur: 0.15, gain: 0.4, highpass: 200 });
  },
  sparkle() {
    [1600, 2100, 2600, 3200].forEach((f,i) => beep({ freq: f, dur: 0.08, type: 'sine', gain: 0.13, delay: i * 0.03 }));
  },
  streakBoom() {
    beep({ freq: 130, dur: 0.4, type: 'sawtooth', gain: 0.32, slideTo: 65 });
    [523, 784, 1047, 1568].forEach((f,i) => beep({ freq: f, dur: 0.22, type: 'triangle', gain: 0.24, delay: 0.15 + i * 0.06 }));
  },
  // Combo escalation — pitch rises each tier (1..5)
  comboHit(tier = 1) {
    const base = 440 + Math.min(tier, 6) * 110;
    beep({ freq: base, dur: 0.09, type: 'square', gain: 0.24 });
    beep({ freq: base * 1.5, dur: 0.07, type: 'triangle', gain: 0.2, delay: 0.05 });
    if (tier >= 3) noise({ dur: 0.08, gain: 0.18, highpass: 2000 });
    if (tier >= 5) {
      beep({ freq: base * 2, dur: 0.12, type: 'sine', gain: 0.22, delay: 0.1 });
      beep({ freq: base * 3, dur: 0.14, type: 'triangle', gain: 0.2, delay: 0.12 });
    }
  },
  // Brainrot "voice" — formant-shaped noise burst approximating a shout
  emote(kind = 'slop') {
    const c = ensureCtx(); if (!c) return;
    const t = c.currentTime;
    const dur = 0.28;
    // Two formants + noise = vowel-ish
    const formants = {
      slop:   [420, 900],  // "SLOP"
      cope:   [500, 1500], // "COPE"
      mog:    [380, 1100], // "MOG"
      ratio:  [520, 1300], // "RATIO"
      goat:   [480, 1200], // "GOAT"
      cook:   [420, 1000], // "COOKED"
      sus:    [560, 1600], // "SUS"
      seethe: [450, 1300], // "SEETHE"
      lore:   [500, 1000], // "LORE"
    }[kind] || [450, 1200];
    formants.forEach((f, i) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = i === 0 ? 'sawtooth' : 'triangle';
      osc.frequency.setValueAtTime(f, t);
      osc.frequency.linearRampToValueAtTime(f * 0.7, t + dur);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.14, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(sfxGain);
      osc.start(t); osc.stop(t + dur + 0.02);
    });
    noise({ dur: 0.1, gain: 0.2, highpass: 1500 });
  },
};

// v1.2: Heartbeat loop for low HP
let heartbeatId = null;
export function startHeartbeat() {
  if (heartbeatId) return;
  const play = () => {
    if (muted) return;
    beep({ freq: 60, dur: 0.13, type: 'sine', gain: 0.45, slideTo: 40 });
    beep({ freq: 55, dur: 0.13, type: 'sine', gain: 0.35, slideTo: 35, delay: 0.16 });
  };
  play();
  heartbeatId = setInterval(play, 900);
}
export function stopHeartbeat() {
  if (heartbeatId) { clearInterval(heartbeatId); heartbeatId = null; }
}

// --- Ambient chiptune loop (v1.3: act-shifting) ---
// Act 1: original synthwave, calmer / warmer
const MUSIC_STEPS_A1 = [ 55, 82, 110, 82, 55, 82, 110, 130, 49, 73, 98, 73, 49, 73, 98, 116 ];
const MUSIC_STEPS_B1 = [ 110, 165, 220, 165, 110, 165, 220, 261, 98,  147, 196, 147, 98,  147, 196, 233 ];
// Act 2: darker minor, slower
const MUSIC_STEPS_A2 = [ 44, 65, 87, 65, 44, 65, 87, 103, 41, 62, 82, 62, 41, 62, 82, 98 ];
const MUSIC_STEPS_B2 = [ 87, 131, 175, 131, 87, 131, 175, 208, 82, 123, 165, 123, 82, 123, 165, 196 ];
// Act 3: dissonant hyperslop, faster arpeggio
const MUSIC_STEPS_A3 = [ 65, 82, 98, 110, 65, 82, 98, 130, 62, 78, 93, 104, 62, 78, 93, 123 ];
const MUSIC_STEPS_B3 = [ 261, 311, 349, 415, 261, 311, 349, 466, 246, 293, 329, 391, 246, 293, 329, 440 ];

let currentAct = 1;
let MUSIC_STEPS_A = MUSIC_STEPS_A1;
let MUSIC_STEPS_B = MUSIC_STEPS_B1;
export function setMusicAct(act) {
  currentAct = Math.max(1, Math.min(3, act|0));
  if (currentAct === 1) { MUSIC_STEPS_A = MUSIC_STEPS_A1; MUSIC_STEPS_B = MUSIC_STEPS_B1; }
  else if (currentAct === 2) { MUSIC_STEPS_A = MUSIC_STEPS_A2; MUSIC_STEPS_B = MUSIC_STEPS_B2; }
  else { MUSIC_STEPS_A = MUSIC_STEPS_A3; MUSIC_STEPS_B = MUSIC_STEPS_B3; }
}
export function getMusicAct() { return currentAct; }

let musicStep = 0;
export function startMusic() {
  const c = ensureCtx();
  if (!c) return;
  if (musicIntervalId) return;
  // Act 3 is 25% faster
  const stepMs = currentAct === 3 ? 150 : (currentAct === 2 ? 230 : 200);
  const play = () => {
    if (muted) return;
    const t = c.currentTime;
    // Bass
    const bf = MUSIC_STEPS_A[musicStep % MUSIC_STEPS_A.length];
    const oscB = c.createOscillator();
    const gB = c.createGain();
    oscB.type = 'sawtooth';
    oscB.frequency.value = bf;
    gB.gain.setValueAtTime(0.09, t);
    gB.gain.exponentialRampToValueAtTime(0.0001, t + 0.19);
    oscB.connect(gB).connect(musicGain);
    oscB.start(t); oscB.stop(t + 0.2);

    // Lead (every 2nd step)
    if (musicStep % 2 === 0) {
      const lf = MUSIC_STEPS_B[musicStep % MUSIC_STEPS_B.length];
      const oscL = c.createOscillator();
      const gL = c.createGain();
      oscL.type = 'triangle';
      oscL.frequency.value = lf;
      gL.gain.setValueAtTime(0.05, t);
      gL.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
      oscL.connect(gL).connect(musicGain);
      oscL.start(t); oscL.stop(t + 0.16);
    }
    // Hi-hat every 4 steps
    if (musicStep % 4 === 3) {
      const bufferSize = Math.floor(c.sampleRate * 0.04);
      const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const src = c.createBufferSource();
      src.buffer = buffer;
      const filt = c.createBiquadFilter();
      filt.type = 'highpass';
      filt.frequency.value = 5000;
      const g = c.createGain();
      g.gain.setValueAtTime(0.04, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      src.connect(filt).connect(g).connect(musicGain);
      src.start(t); src.stop(t + 0.06);
    }
    musicStep++;
  };
  musicIntervalId = setInterval(play, stepMs);
}

export function stopMusic() {
  if (musicIntervalId) {
    clearInterval(musicIntervalId);
    musicIntervalId = null;
  }
}

// v2.9: UPBEAT TECHNO MENU MUSIC — 128 BPM 4-on-the-floor kick, bassline, hi-hats, arp
let menuMusicId = null;
// A-minor techno bassline (16 steps, one bar) — pumps a driving loop
const MENU_BASS   = [110, 110,   0, 110,   0, 110, 110,   0, 110,   0, 110,   0, 110, 110,   0, 130];
// Arpeggio on top (16 steps): Am7 arp with occasional off-note for cursed swagger
const MENU_ARP    = [440, 523, 659, 880, 659, 523, 440, 587, 659, 880, 1046, 880, 659, 523, 440, 587];
// Kick pattern: 4-on-the-floor (steps 0, 4, 8, 12)
const MENU_KICK   = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0];
// Hi-hat pattern: off-beat + syncopation
const MENU_HAT    = [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1];
// Snare/clap on 2 & 4
const MENU_CLAP   = [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0];
let menuStep = 0;

function techKick(t) {
  const c = ensureCtx(); if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(140, t);
  osc.frequency.exponentialRampToValueAtTime(35, t + 0.12);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.55, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
  osc.connect(g).connect(musicGain);
  osc.start(t); osc.stop(t + 0.28);
  // Click layer for punch
  const click = c.createOscillator();
  const cg = c.createGain();
  click.type = 'square';
  click.frequency.setValueAtTime(1600, t);
  cg.gain.setValueAtTime(0.18, t);
  cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
  click.connect(cg).connect(musicGain);
  click.start(t); click.stop(t + 0.03);
}

function techHat(t, open = false) {
  const c = ensureCtx(); if (!c) return;
  const dur = open ? 0.11 : 0.03;
  const bufferSize = Math.floor(c.sampleRate * dur);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filt = c.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = 7000;
  const g = c.createGain();
  g.gain.setValueAtTime(open ? 0.14 : 0.08, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filt).connect(g).connect(musicGain);
  src.start(t); src.stop(t + dur + 0.01);
}

function techClap(t) {
  const c = ensureCtx(); if (!c) return;
  // Layered noise bursts approximate a clap
  for (let k = 0; k < 3; k++) {
    const dur = 0.08;
    const bufferSize = Math.floor(c.sampleRate * dur);
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filt = c.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.value = 1500;
    filt.Q.value = 1.2;
    const g = c.createGain();
    const t0 = t + k * 0.012;
    g.gain.setValueAtTime(0.16, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filt).connect(g).connect(musicGain);
    src.start(t0); src.stop(t0 + dur + 0.01);
  }
}

function techBass(t, freq) {
  const c = ensureCtx(); if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, t);
  // Sub-bass sine layer
  const sub = c.createOscillator();
  const sg = c.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(freq / 2, t);
  // Low-pass filter for that classic techno bass tone
  const filt = c.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.setValueAtTime(600, t);
  filt.frequency.exponentialRampToValueAtTime(200, t + 0.14);
  filt.Q.value = 6;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.24, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
  sg.gain.setValueAtTime(0.16, t);
  sg.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
  osc.connect(filt).connect(g).connect(musicGain);
  sub.connect(sg).connect(musicGain);
  osc.start(t); osc.stop(t + 0.2);
  sub.start(t); sub.stop(t + 0.2);
}

function techArp(t, freq) {
  const c = ensureCtx(); if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(freq, t);
  const filt = c.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.value = 2800;
  filt.Q.value = 3;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.09, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
  osc.connect(filt).connect(g).connect(musicGain);
  osc.start(t); osc.stop(t + 0.12);
}

export function startMenuMusic() {
  const c = ensureCtx(); if (!c) return;
  if (menuMusicId) return;
  // 128 BPM → 16th notes = 60000 / 128 / 4 ≈ 117ms per step
  const stepMs = 117;
  const tick = () => {
    if (muted) return;
    const t = c.currentTime + 0.02;
    const step = menuStep % 16;
    if (MENU_KICK[step]) techKick(t);
    if (MENU_HAT[step])  techHat(t, step % 4 === 3); // open hat on the "and-of-4"
    if (MENU_CLAP[step]) techClap(t);
    if (MENU_BASS[step]) techBass(t, MENU_BASS[step]);
    // Arp every 2 steps for busier top
    if (step % 2 === 0) techArp(t, MENU_ARP[step]);
    menuStep++;
  };
  tick();
  menuMusicId = setInterval(tick, stepMs);
}
export function stopMenuMusic() {
  if (menuMusicId) { clearInterval(menuMusicId); menuMusicId = null; }
}

export function setMuted(m) {
  muted = m;
  ensureCtx();
  if (masterGain) masterGain.gain.value = m ? 0 : (getMasterVolume());
}

export function isMuted() { return muted; }

// v2.6 QoL — separate volume sliders (persisted via localStorage). Applied lazily.
function _read(key, def) {
  try { const v = parseFloat(localStorage.getItem(key)); return isNaN(v) ? def : v; } catch (e) { return def; }
}
function getMasterVolume() { return _read('friendslop.vol.master', 0.6); }
function getMusicVolume() { return _read('friendslop.vol.music', 0.18); }
function getSfxVolume() { return _read('friendslop.vol.sfx', 0.6); }

export function setMasterVolume(v) {
  ensureCtx();
  try { localStorage.setItem('friendslop.vol.master', String(v)); } catch (e) {}
  if (masterGain) masterGain.gain.value = muted ? 0 : v;
}
export function setMusicVolume(v) {
  ensureCtx();
  try { localStorage.setItem('friendslop.vol.music', String(v)); } catch (e) {}
  if (musicGain) musicGain.gain.value = v;
}
export function setSfxVolume(v) {
  ensureCtx();
  try { localStorage.setItem('friendslop.vol.sfx', String(v)); } catch (e) {}
  if (sfxGain) sfxGain.gain.value = v;
}
export function getVolumes() {
  return { master: getMasterVolume(), music: getMusicVolume(), sfx: getSfxVolume() };
}

